import React, { useEffect, useMemo, useState } from 'react'

import Select from 'react-select'
import { toast } from 'react-toastify'

import {
  setIsKickBackModalOpen,
  setKickBackApplication,
  setStages,
} from '../DealKickBack/kickBackSlice'
import { setClosedLossDrawerOpen } from '@/client/generalSlice'
import { useAppDispatch } from '@/client/hooks'
import { graphqlApi } from '@/graphql'
import { Application } from '@/graphql/generated'
import { useUpdateSubStageMutation } from '@/services/application'
import { useGetEscalationsQuery } from '@/services/escalations'
import { useGetLoggedUserQuery } from '@/services/user'
import {
  useGetChecklistGroupsQuery,
  useUpdateItemsFromTemplateMutation,
} from '@/services/v2-application/checklistGroup'
import {
  calcIsSelectDisabled,
  customStyles,
  shouldOpenKickBackModal,
  useGenerateOptions,
} from '@/utils/StageSelector'

type StageSelectorProps = {
  application: Application
}

export const StageSelector = ({ application }: StageSelectorProps) => {
  const dispatch = useAppDispatch()
  const [updateSubStage, { isLoading: isUpdating }] = useUpdateSubStageMutation()
  const [updateItemsFromTemplate] = useUpdateItemsFromTemplateMutation()
  const { data: user, isLoading: isLoadingUser } = useGetLoggedUserQuery()
  const { data: escalations } = useGetEscalationsQuery(application?.externalId, {
    skip: !application?.externalId,
  })
  const [actualState, setActualState] = useState(application?.applicationState)
  const [hasEscalationsNotApproved, setHasEscalationsNotApproved] = useState(false)

  useEffect(() => {
    setHasEscalationsNotApproved(escalations?.some((escalation) => !escalation.approved))
  }, [escalations])

  useEffect(() => {
    if (application) setActualState(application?.applicationState)
  }, [application?.applicationState])

  const { data: checklist_groups } = useGetChecklistGroupsQuery(application?.uuid, {
    skip: !application.uuid,
  })
  const isDisabled = useMemo(
    () => calcIsSelectDisabled(user, application, actualState),
    [application]
  )

  const options = useGenerateOptions(
    application?.uuid,
    application?.applicationState,
    application.applicationStateIndex,
    user,
    isLoadingUser,
    hasEscalationsNotApproved
  )

  const handleChange = async (option) => {
    const actual = options.find((it) => it.value === actualState)

    if (shouldOpenKickBackModal(option, user, actualState)) {
      dispatch(setKickBackApplication(application?.externalId))
      dispatch(setIsKickBackModalOpen(true))
      dispatch(setStages({ from: actual, to: option }))
      return
    }

    if (option.value !== 'closed-lost') {
      const destinationGroup = checklist_groups?.results?.find(
        (item) => item.type_name === option.label
      )

      if (destinationGroup && option.index > application.applicationStateIndex) {
        // Update checklist group with the latest items from template
        await updateItemsFromTemplate({
          applicationId: application.uuid,
          checklistGroupId: destinationGroup.uuid,
        })
      }

      const response = await updateSubStage({
        external_id: application.externalId,
        application_state: option.value,
      })

      if ('data' in response) {
        setActualState(response?.data?.application_state)
      }

      if ('error' in response) {
        const errorDetail =
          (response?.error && 'data' in response.error && (response.error as any).data?.detail) ||
          'Something went wrong.'
        toast.error(errorDetail, {
          containerId: 'global',
          position: 'top-center',
        })
        return
      }
      dispatch(graphqlApi.util.invalidateTags(['gql:application']))
    } else {
      dispatch(setClosedLossDrawerOpen(true))
    }
  }

  return (
    <>
      <Select
        instanceId='category-select'
        options={options}
        defaultValue={options.find((it) => it.value === application?.applicationState)}
        onChange={handleChange}
        isDisabled={isUpdating || isDisabled}
        isOptionDisabled={(option) => option.disabled}
        isLoading={isUpdating}
        isSearchable={false}
        className='font-bold text-[12px] 3xl:text-[14px]'
        styles={customStyles}
        value={options.find((it) => it.value === actualState)}
      />
    </>
  )
}
